import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { LoadingIndicator } from '../../../components/Tools/LoadingIndicator';
import { Button } from '../../../ui/button';
import { planDetails, PLAN_IDS, BILLING_CYCLES, getYearlySavings, getYearlyDiscountPercentage, getStripePriceId } from '../../../constants/plans';
import { toast } from 'react-toastify';
import axios from 'axios';
import useLocalStorage from 'use-local-storage';
import { signOut } from 'firebase/auth';
import { auth } from '../../../firebase';
import { useNavigate } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';
import { CgProfile } from "react-icons/cg";
import { FaFacebookF, FaGoogle, FaInstagram, FaRegLightbulb, FaSearch, FaUserFriends, FaYoutube, FaCreditCard, FaSync } from 'react-icons/fa';
import { RiAdvertisementFill } from 'react-icons/ri';
import { BsTwitterX } from 'react-icons/bs';
import { IoShareSocialSharp } from "react-icons/io5";
import Socials from '../../Protected/Socials/Socials';
import TestimonialsCarousel from '../../../components/Tools/TestimonialsCarousel';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const SubscriptionOnboarding = () => {
  const { idToken, user, activeWorkspace } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState(BILLING_CYCLES.MONTHLY);
  const [userEmail, setUserEmail] = useState('');
  const [theme] = useLocalStorage("theme", "light");
  const navigate = useNavigate();
  
  // Step management
  const [activeStep, setActiveStep] = useState(0);
  const [userSettings, setUserSettings] = useState(null);
  const [steps, setSteps] = useState([
    { 
      label: 'Your Role', 
      mobileLabel: 'Role',
      completed: false, 
      icon: <CgProfile size={24} color='white' /> 
    },
    { 
      label: 'How did you find us?', 
      mobileLabel: 'Discovery',
      completed: false, 
      icon: <FaSearch size={24} color='white' /> 
    },
    { 
      label: 'Connect Socials', 
      mobileLabel: 'Socials',
      completed: false, 
      icon: <IoShareSocialSharp size={24} color='white' /> 
    },
    { 
      label: 'Choose Plan', 
      mobileLabel: 'Plan',
      completed: false, 
      icon: <FaCreditCard size={24} color='white' /> 
    },
  ]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedHow, setSelectedHow] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [isRefreshingSocials, setIsRefreshingSocials] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const hasRunOnceRef = useRef(false);
  const [username] = useLocalStorage('username', '');

  useEffect(() => {
    if (user && user.email) {
      setUserEmail(user.email);
    }
  }, [user]);

  // Step navigation functions
  const handleNext = () => setActiveStep(prevStep => prevStep + 1);
  const handleBack = () => setActiveStep(prevStep => prevStep - 1);
  const handleStepClick = (index) => {
    setActiveStep(index);
  };

  // Get user's onboarding data
  const getUsersOnboarding = useCallback(async () => {
    try {
      const response = await axios.get(BACKEND_API_URL + '/api/auth/user/settings', {
        params: { workspaceId: activeWorkspace?.id },
        headers: { Authorization: `Bearer ${idToken}` }
      });
      
      setUserSettings(response.data);
      
      if(response.data && response.data.onboarding) {
        const { role, foundFrom, socialsConnected, user_role, user_discover } = response.data.onboarding;
        
        // Use either the new or old field names
        const userRole = role || user_role;
        const userDiscover = foundFrom || user_discover;
        
        let nextIncompleteStep = 0;
        
        // Step 1: Role
        if(userRole) {
          setSelectedRole(userRole);
          setSteps(prevSteps => {
            const updatedSteps = [...prevSteps];
            updatedSteps[0].completed = true;
            return updatedSteps;
          });
          nextIncompleteStep = 1;
        }
        
        // Step 2: Discovery
        if(userDiscover) {
          setSelectedHow(userDiscover);
          setSteps(prevSteps => {
            const updatedSteps = [...prevSteps];
            updatedSteps[1].completed = true;
            return updatedSteps;
          });
          nextIncompleteStep = 2;
        }
        
        // Step 3: Socials (optional, but if marked as done, skip to payment)
        if(socialsConnected) {
          setSteps(prevSteps => {
            const updatedSteps = [...prevSteps];
            updatedSteps[2].completed = true;
            return updatedSteps;
          });
          nextIncompleteStep = 3;
        }
        
        // If user has completed steps 1 and 2, jump to where they left off
        // If they haven't paid yet, go to payment step
        if(userRole && userDiscover) {
          setActiveStep(nextIncompleteStep);
        }
      }
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  }, [idToken, activeWorkspace]);

  // Handle manual refresh of socials
  const handleRefreshSocials = useCallback(async () => {
    try {
      setIsRefreshingSocials(true);
      // Trigger refresh in the Socials component
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error refreshing social media tokens:', error);
    } finally {
      setIsRefreshingSocials(false);
    }
  }, []);

  // Listen for localStorage changes from other tabs (social auth completion)
  useEffect(() => {
    // Function to check if social auth was successful
    const checkAuthSuccess = () => {
      const authSuccess = localStorage.getItem('social_auth_success');
      if (authSuccess) {
        // Clear the flag
        localStorage.removeItem('social_auth_success');
        // Trigger refresh in the Socials component
        setRefreshTrigger(prev => prev + 1);
      }
    };

    // Function to handle focus events for pending auth
    const handleFocus = () => {
      const pendingAuth = localStorage.getItem('social_auth_pending');
      if (pendingAuth) {
        // When the user returns to this tab, check if there are pending auths and refresh
        localStorage.removeItem('social_auth_pending');
        // Trigger refresh in the Socials component
        setRefreshTrigger(prev => prev + 1);
      }
    };

    // Check immediately (for when returning to an already-open tab)
    checkAuthSuccess();

    // Set up event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'social_auth_success') {
        checkAuthSuccess();
      }
    };

    // Listen for when the window regains focus
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up the event listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!hasRunOnceRef.current && idToken) {
      hasRunOnceRef.current = true;
      getUsersOnboarding();
    }
  }, [idToken, activeWorkspace, getUsersOnboarding]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  // Step submission handlers
  const handleStep1Submit = async (activeRole) => {
    if (activeRole !== '') {
      setSelectedRole(activeRole);
      setSteps(prevSteps => {
        const updatedSteps = [...prevSteps];
        updatedSteps[0].completed = true;
        return updatedSteps;
      });
      
      // Save progress immediately
      try {
        await axios.post(BACKEND_API_URL + '/api/auth/user/settings', {
          onboarding: {
            role: activeRole,
            user_role: activeRole,
            stepCompleted: 1
          },
          workspaceId: activeWorkspace?.id
        }, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
      } catch (error) {
        console.error('Error saving step 1:', error);
      }
      
      handleNext();
    }
  };

  const handleStep2Submit = async (activeHow) => {
    if (activeHow !== '') {
      setSelectedHow(activeHow);
      setSteps(prevSteps => {
        const updatedSteps = [...prevSteps];
        updatedSteps[1].completed = true;
        return updatedSteps;
      });
      
      // Save progress with both role and discovery
      const onboarding = {
        onboarding: {
          role: selectedRole,
          user_role: selectedRole,
          foundFrom: activeHow,
          user_discover: activeHow,
          stepCompleted: 2
        },
        workspaceId: activeWorkspace?.id
      };

      try {
        await axios.post(BACKEND_API_URL + '/api/auth/user/settings', onboarding, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
      } catch (error) {
        console.error('Error saving step 2:', error);
      }
      
      handleNext();
    }
  };

  const handleStep3Submit = async () => {
    setSteps(prevSteps => {
      const updatedSteps = [...prevSteps];
      updatedSteps[2].completed = true;
      return updatedSteps;
    });
    
    // Save progress that socials step is completed
    try {
      await axios.post(BACKEND_API_URL + '/api/auth/user/settings', {
        onboarding: {
          role: selectedRole,
          user_role: selectedRole,
          foundFrom: selectedHow,
          user_discover: selectedHow,
          socialsConnected: true,
          stepCompleted: 3
        },
        workspaceId: activeWorkspace?.id
      }, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
    } catch (error) {
      console.error('Error saving step 3:', error);
    }
    
    handleNext();
  };

  const handleStep4Submit = (planId) => {
    setSelectedPlan(planId);
    setSteps(prevSteps => {
      const updatedSteps = [...prevSteps];
      updatedSteps[3].completed = true;
      return updatedSteps;
    });
    // Proceed with subscription
    handleSubscription(planId);
  };

  // Track InitiateCheckout event for Facebook Conversion API
  const trackInitiateCheckoutEvent = async (planId, cycle) => {
    try {
      const planName = planDetails[planId]?.name || 'Basic';
      const planPrice = planDetails[planId]?.price[cycle] || 0;

      await axios.post(
        `${BACKEND_API_URL}/api/auth/user/track-initiatecheckout`,
        {
          planId,
          planName,
          billingCycle: cycle,
          price: planPrice,
          workspaceId: activeWorkspace?.id
        },
        {
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );

    } catch (error) {
      console.error('Error tracking InitiateCheckout event:', error);
      // Don't block the checkout process if tracking fails
    }
  };

  // Track AddToCart event for Facebook Conversion API
  const trackAddToCartEvent = async (planId, cycle) => {
    try {
      const planName = planDetails[planId]?.name || 'Basic';
      const planPrice = planDetails[planId]?.price[cycle] || 0;

      await axios.post(
        `${BACKEND_API_URL}/api/auth/user/track-addtocart`,
        {
          planId,
          planName,
          billingCycle: cycle,
          price: planPrice,
          workspaceId: activeWorkspace?.id
        },
        {
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );

    } catch (error) {
      console.error('Error tracking AddToCart event:', error);
      // Don't block the checkout process if tracking fails
    }
  };

  // Handle subscription with 3-day free trial
  const handleSubscription = async (planId) => {
    try {
      if (!idToken) {
        toast.error("Please log in to subscribe");
        return;
      }

      setLoading(true);

      // Server-side tracking via Facebook Conversion API
      trackAddToCartEvent(planId, billingCycle);
      trackInitiateCheckoutEvent(planId, billingCycle);

      // Get Stripe Price ID for the selected plan and billing cycle
      const priceId = getStripePriceId(planId, billingCycle);

      if (!priceId) {
        toast.error("Price ID not found for this plan");
        return;
      }

      // Create checkout session with trial
      const response = await axios.post(
        `${BACKEND_API_URL}/api/stripe/create-checkout-session-with-trial`,
        {
          priceId: priceId,
          workspaceId: activeWorkspace?.id,
          customerEmail: userEmail,
          trialPeriodDays: 3 // 3-day free trial
        },
        {
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );

      if (response.data && response.data.url) {
        // Redirect to Stripe checkout (same window to prevent popup blockers)
        window.location.href = response.data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error("Failed to redirect to checkout page");
    } finally {
      setLoading(false);
    }
  };

  // Check if a plan should be highlighted (recommended)
  const isPlanHighlighted = (planId) => {
    return planId === PLAN_IDS.CREATOR; // Highlight Creator plan for new users
  };

  const logoSrc = theme === 'light' ? 'logo/logo-vertical-black.png' : 'logo/logo-vertical-bkg.png';

  return (
    <div className={`flex flex-col min-h-screen --theme-colors ${theme === 'light' ? 'background-gradient' : 'dark-background-gradient'}`}>
            {/* Header */}
      <div className="w-full px-4 py-3 sm:px-6 flex justify-between items-center">
        <img src={`${process.env.PUBLIC_URL}/${logoSrc}`} className="w-[140px] sm:w-[200px]" alt="logo" />
        
        {/* User info and logout */}
        <div className="flex items-center gap-2 sm:gap-4">
          {user && (
            <div className="text-xs sm:text-sm text-gray-600 hidden sm:block">
              Signed in as <span className="font-medium">{user.email}</span>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="flex items-center gap-1 sm:gap-2 text-sm"
          >
            <FiLogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
            <span className="sm:hidden">Exit</span>
          </Button>
        </div>
      </div>

      {/* Step-based Content */}
      <div className="flex-1 px-2 sm:px-4 py-6 md:px-8">
        <div className="max-w-6xl mx-auto w-full">
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4">
              Welcome to FrenzyPost{username !== "" && (
                <span>, {username}</span>
              )}! 🚀
            </h1>
            <p className="text-xl text-gray-600 mb-2">
              Let's get you set up in just a few quick steps
            </p>
            <p className="text-sm italic text-gray-500">
              Your answers help us personalize and optimize your experience
            </p>
          </div>

          {/* Step Progress Indicator */}
          <div className="mb-8 px-2 sm:px-4">
            <div className="flex items-center justify-between w-full max-w-2xl mx-auto">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <div className="flex flex-col items-center gap-1 sm:gap-2 flex-1">
                    <div 
                      className={`p-2 sm:p-3 rounded-full cursor-pointer transform hover:scale-105 transition-all duration-200 ${
                        step.completed 
                          ? 'bg-gradient-to-r from-blue-400 to-blue-700' 
                          : index === activeStep 
                            ? 'bg-blue-500' 
                            : 'bg-gray-300'
                      }`} 
                      onClick={() => handleStepClick(index)}
                    >
                      <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                        <div className="sm:hidden">
                          {React.cloneElement(step.icon, { size: 16 })}
                        </div>
                        <div className="hidden sm:block">
                          {React.cloneElement(step.icon, { size: 20 })}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-center leading-tight px-1 min-h-[32px] sm:min-h-[40px] flex items-center justify-center">
                      <span className="sm:hidden">
                        {step.mobileLabel}
                      </span>
                      <span className="hidden sm:block text-center">
                        {index === 0 && (
                          <>
                            Your<br />Role
                          </>
                        )}
                        {index === 1 && (
                          <>
                            How did you<br />find us?
                          </>
                        )}
                        {index === 2 && (
                          <>
                            Connect<br />Socials
                          </>
                        )}
                        {index === 3 && (
                          <>
                            Choose<br />Plan
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 sm:mx-3 rounded transition-all duration-300 ${
                      step.completed 
                        ? 'bg-gradient-to-r from-blue-700 to-blue-300' 
                        : 'bg-gray-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          {activeStep === 0 && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-400/50 p-6 sm:p-8 min-h-[500px] transition-all duration-300 hover:bg-white/90">
              <SubscriptionStep1 
                onSubmit={handleStep1Submit} 
                active={selectedRole} 
              />
            </div>
          )}
          {activeStep === 1 && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-400/50 p-6 sm:p-8 min-h-[500px] transition-all duration-300 hover:bg-white/90">
              <SubscriptionStep2 
                onSubmit={handleStep2Submit} 
                active={selectedHow} 
                onBack={handleBack} 
              />
            </div>
          )}
          {activeStep === 2 && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-400/50 p-6 sm:p-8 min-h-[500px] transition-all duration-300 hover:bg-white/90">
              <SubscriptionStep3 
                onSubmit={handleStep3Submit} 
                onBack={handleBack}
                onRefreshSocials={handleRefreshSocials}
                isRefreshing={isRefreshingSocials}
                refreshTrigger={refreshTrigger}
              />
            </div>
          )}
        </div>
      </div>

      {/* Step 4 - Full Width Payment Section */}
      {activeStep === 3 && (
        <SubscriptionStep4 
          onSubmit={handleStep4Submit} 
          onBack={handleBack}
          billingCycle={billingCycle}
          setBillingCycle={setBillingCycle}
          loading={loading}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-40">
          <LoadingIndicator show={true} />
        </div>
      )}
    </div>
  );
};

// Step 1: Role Selection
const SubscriptionStep1 = ({ onSubmit, active }) => {
  const [activeRole, setActiveRole] = useState(active || '');
  const hasRunOnceRef = useRef(false);
  const { idToken } = useWorkspace();

  useEffect(() => {
    if (!hasRunOnceRef.current && idToken) {
      hasRunOnceRef.current = true;
      setActiveRole(active || '');
    }
  }, [active, idToken]);

  const handleRoleClick = (role) => {
    setActiveRole(role.alt);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(activeRole);
  };

  const roles = [
    {
      label: "Creator / Influencer",
      image: "./onboarding/influencer.png",
      alt: "Influencer",
      description: "You manage your own social media channels and work on your own brand or for entertainment."
    },
    {
      label: "Startup / Small Business",
      image: "./onboarding/shop.png",
      alt: "Startup",
      description: "You have started a startup business or are running a small business."
    },
    {
      label: "Freelancer",
      image: "./onboarding/freelance.png",
      alt: "Freelancer",
      description: "You manage social media for more than one individual clients other than yourself."
    },
    {
      label: "Agency",
      image: "./onboarding/enterprise.png",
      alt: "Agency",
      description: "You manage multiple social media accounts simultaneously as part of an agency."
    },
    {
      label: "Large Business",
      image: "./onboarding/company.png",
      alt: "Business",
      description: "You represent a large corporation (over 50 employees) with complex social media management needs, overseeing multiple accounts."
    },
    {
      label: "Other",
      image: "./onboarding/other.png",
      alt: "Other",
      description: "None of the above."
    }
  ];

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="flex flex-col gap-4 flex-1">
        <div className="text-center mb-6">
          <p className="text-sm uppercase italic text-gray-500 mb-2">STEP 1 OF 3</p>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Which role best describes you?</h2>
          <p className="text-gray-600">Help us personalize your FrenzyPost experience</p>
        </div>

        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 flex-1">
          {roles.map((role, index) => (
            <div 
              key={index} 
              className={`flex flex-col items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                role.alt === activeRole 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-300'
              }`} 
              onClick={() => handleRoleClick(role)}
            >
              <div className="w-16 h-16 mb-3">
                <img src={role.image} alt={role.alt} className="w-full h-full object-contain" />
              </div>
              <div className="font-semibold text-center mb-2">{role.label}</div>
              <div className="text-sm text-gray-600 text-center">{role.description}</div>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-6">
          <Button
            type="submit"
            disabled={activeRole === ''}
            className={`px-8 py-3 ${
              activeRole === '' 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            Next
          </Button>
        </div>
      </div>
    </form>
  );
};

// Step 2: Discovery Source
const SubscriptionStep2 = ({ onSubmit, active, onBack }) => {
  const [activeHow, setActiveHow] = useState(active || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(activeHow);
  };

  const handleClick = (how) => {
    setActiveHow(how);
  };

  const hows = [
    { title: 'Facebook', icon: <FaFacebookF color='#1877F2' size={32} /> },
    { title: 'Youtube', icon: <FaYoutube color='#FF0000' size={32} /> },
    { title: 'Twitter', icon: <BsTwitterX color='black' size={32} /> },
    { title: 'Instagram', icon: <FaInstagram color='#E4405F' size={32} /> },
    { title: 'Google', icon: <FaGoogle color='#4285F4' size={32} /> },
    { title: 'Referral', icon: <FaUserFriends color='#6366F1' size={32} /> },
    { title: 'Advertisement', icon: <RiAdvertisementFill color='#3B82F6' size={32} /> },
    { title: 'Other', icon: <FaRegLightbulb color='#F59E0B' size={32} /> }
  ];

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="flex flex-col gap-4 flex-1">
        <div className="text-center mb-6">
          <p className="text-sm uppercase italic text-gray-500 mb-2">STEP 2 OF 3</p>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">How did you discover FrenzyPost?</h2>
          <p className="text-gray-600">This helps us understand our audience better</p>
        </div>

        <div className="grid gap-4 grid-cols-2 md:grid-cols-4 flex-1">
          {hows.map((how, index) => (
            <div 
              key={index} 
              className={`flex flex-col items-center justify-center p-6 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                how.title === activeHow 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-300'
              }`} 
              onClick={() => handleClick(how.title)}
            >
              <div className="mb-3">
                {how.icon}
              </div>
              <div className="font-medium text-center">{how.title}</div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-6">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            className="px-8 py-3"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={activeHow === ''}
            className={`px-8 py-3 ${
              activeHow === '' 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            Next
          </Button>
        </div>
      </div>
    </form>
  );
};

// Step 3: Connect Socials
const SubscriptionStep3 = ({ onSubmit, onBack, onRefreshSocials, isRefreshing, refreshTrigger }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const finishOnboarding = () => {
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="flex flex-col gap-4 flex-1">
        <div className="text-center mb-6">
          <p className="text-sm uppercase italic text-gray-500 mb-2">STEP 3 OF 4</p>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Connect to your social media accounts</h2>
          <p className="text-gray-600">Connect your accounts to start posting automatically</p>
          
          {/* Refresh Button */}
          <div className="flex justify-center mt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRefreshSocials}
              disabled={isRefreshing}
              className="flex items-center gap-2"
            >
              <FaSync className={`${isRefreshing ? 'animate-spin' : ''}`} size={12} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Socials'}
            </Button>
          </div>
        </div>

         <div className="flex-1">
           <Socials value={'dashboard'} refreshTrigger={refreshTrigger} />
         </div>

        <div className="flex justify-between mt-6">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            className="px-8 py-3"
          >
            Back
          </Button>
          <div className="flex items-end gap-2">
            <div 
              className="text-sm hover:underline cursor-pointer hover:text-blue-300 text-right" 
              onClick={finishOnboarding}
            >
              Skip for now
            </div>
             <Button
               type="submit"
               className="px-8 py-3 bg-blue-500 hover:bg-blue-600"
             >
               Next
             </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

// Step 4: Plan Selection with all original content
const SubscriptionStep4 = ({ onSubmit, onBack, billingCycle, setBillingCycle, loading }) => {
  const [selectedPlan, setSelectedPlan] = useState('');

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    onSubmit(planId);
  };

  // Check if a plan should be highlighted (recommended)
  const isPlanHighlighted = (planId) => {
    return planId === PLAN_IDS.CREATOR;
  };

  return (
    <div className="w-full">
      {/* Main Content */}
      <div className="flex-1 px-4 py-6 md:px-8">
        <div className="max-w-6xl mx-auto w-full">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm uppercase italic text-gray-500 mb-2">STEP 4 OF 4</p>
            <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
            <p className="text-xl text-gray-600 mb-2">
              Choose your plan to start creating amazing content
            </p>
            <p className="text-lg text-green-600 font-semibold">
              ✨ Start with a 3-day FREE trial on any plan ✨
            </p>
            <p className="text-sm text-gray-600 font-medium mt-2">
              💡 Pro tip: Choose yearly billing to save {getYearlyDiscountPercentage()}% on your subscription!
            </p>
          </div>

          {/* Billing cycle toggle */}
          <div className="flex justify-center mb-8 px-4">
            <div className="inline-flex items-center p-1 rounded-md bg-gray-100 w-full max-w-md sm:w-auto relative">
              <button 
                type="button"
                className={`px-3 sm:px-4 py-2 rounded-md !text-gray-900 flex-1 sm:flex-none text-sm sm:text-base transition-all duration-200 ${
                  billingCycle === BILLING_CYCLES.MONTHLY ? 'bg-white shadow-sm' : 'hover:bg-gray-50'
                }`}
                onClick={() => setBillingCycle(BILLING_CYCLES.MONTHLY)}
              >
                Monthly
              </button>
              <button 
                type="button"
                className={`px-3 sm:px-4 py-2 rounded-md flex items-center justify-center !text-gray-900 flex-1 sm:flex-none text-sm sm:text-base transition-all duration-200 ${
                  billingCycle === BILLING_CYCLES.YEARLY ? 'bg-white shadow-sm' : 'hover:bg-gray-50'
                }`}
                onClick={() => setBillingCycle(BILLING_CYCLES.YEARLY)}
              >
                Yearly
                <span className={`ml-1 sm:ml-2 text-xs px-1 sm:px-2 py-1 rounded-full font-medium ${
                  billingCycle === BILLING_CYCLES.YEARLY ? 'bg-green-100 text-green-800' : 'bg-green-100 text-green-800'
                }`}>
                  Save {getYearlyDiscountPercentage()}%
                </span>
              </button>
              
              {/* Subtle hint for yearly discount */}
              {billingCycle === BILLING_CYCLES.MONTHLY && (
                <div className="absolute -top-1 -right-1">
                  <div className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full font-medium">
                    💡
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Subtle Yearly Savings Hint - Only show when monthly is selected */}
          {billingCycle === BILLING_CYCLES.MONTHLY && (
            <div className="mb-6 px-2 sm:px-0">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-sm text-blue-700">
                  💡 <strong>Save {getYearlyDiscountPercentage()}%</strong> with yearly billing • 
                  <button 
                    type="button"
                    onClick={() => setBillingCycle(BILLING_CYCLES.YEARLY)}
                    className="ml-1 text-blue-600 underline hover:text-blue-800 font-medium"
                  >
                    Switch to yearly
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8 px-2 sm:px-0">
            {Object.values(planDetails).map((plan) => (
              <div
                key={plan.id}
                className={`border rounded-lg p-6 flex flex-col relative cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  isPlanHighlighted(plan.id)
                    ? 'border-blue-500 shadow-lg bg-blue-50 text-gray-900 transform scale-105'
                    : 'border-gray-200 shadow-md hover:border-blue-300'
                }`}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {isPlanHighlighted(plan.id) && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-bold mb-2 ${isPlanHighlighted(plan.id) ? '!text-gray-900' : ''}`}>
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className={`text-4xl font-bold mb-2 ${isPlanHighlighted(plan.id) ? '!text-gray-900' : ''}`}>
                    ${plan.price[billingCycle]}
                    <span className="text-lg font-normal !text-gray-500">
                      /{billingCycle === BILLING_CYCLES.MONTHLY ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingCycle === BILLING_CYCLES.YEARLY && (
                    <div className="mb-3">
                      <p className="text-sm text-green-600 font-semibold">
                        Save ${getYearlySavings(plan.id)} yearly
                      </p>
                    </div>
                  )}
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold mb-6">
                    3-Day FREE Trial
                  </div>
                </div>

                <ul className="flex-grow space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className={`text-sm ${isPlanHighlighted(plan.id) ? '!text-gray-900' : ''}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  disabled={loading}
                  className={`w-full py-3 text-lg font-semibold ${
                    isPlanHighlighted(plan.id)
                      ? 'bg-blue-500 hover:bg-blue-600 text-white'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  {loading ? 'Processing...' : 'Start 3-Day FREE Trial'}
                </Button>
              </div>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="text-center text-gray-600 mb-8">
            <p className="mb-2">⚡ Cancel anytime during trial - no questions asked</p>
            <p>💳 No hidden fees or commitments</p>
          </div>
        </div>
      </div>

      {/* Testimonials Carousel - Outside parent container for full width */}
      <div className="mt-12 mb-8">
        <TestimonialsCarousel />
      </div>

      {/* Main Content - Continue with constrained container */}
      <div className="flex-1 px-4 py-6 md:px-8">
        <div className="max-w-6xl mx-auto w-full">

          {/* YouTube Video Guide */}
          <div className="mt-8 mb-8 px-2 sm:px-0">
            <h3 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800 text-center">
              🎥 See FrenzyPost in Action
            </h3>
            <div className="max-w-4xl mx-auto">
              <div className="relative w-full h-0 pb-[56.25%] rounded-lg overflow-hidden shadow-lg">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/L-YWXhCrYxQ?si=v_21jrTXQXISjmz9"
                  title="FrenzyPost Platform Guide"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-sm text-gray-600 mt-3 text-center">
                Watch this quick overview to see how FrenzyPost helps you create and schedule content across all your social media platforms
              </p>
            </div>
          </div>

          {/* Manual Posting vs FrenzyPost Comparison */}
          <div className="mt-12 mb-8 px-2 sm:px-0">
            <h3 className="text-2xl sm:text-3xl font-bold mb-8 text-gray-800 text-center">
              Stop Wasting Hours on Manual Posting! ⏰
            </h3>
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                
                {/* FrenzyPost (Automated) */}
                <div className="bg-gradient-to-br from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-6 relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      With FrenzyPost
                    </span>
                  </div>
                  <div className="mt-4">
                    <h4 className="text-2xl font-bold text-gray-800 mb-6 text-center">🚀 Work Smarter</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>5 Minutes Daily</strong> - Schedule a week's content in minutes</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>AI Content Creation</strong> - Generate posts automatically</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>Post to 8+ Platforms</strong> - All from one dashboard</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>Never Miss a Post</strong> - Automated scheduling</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>Consistent Posting</strong> - Even when you're sleeping</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>Professional Results</strong> - High-quality, engaging content</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>Focus on Business</strong> - More time for what matters</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-xl">✅</span>
                        <span className="text-gray-800"><strong>Analytics & Insights</strong> - Track performance automatically</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Manual Posting */}
                <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gray-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Manual Posting
                    </span>
                  </div>
                  <div className="mt-4">
                    <h4 className="text-2xl font-bold text-gray-800 mb-6 text-center">😫 The Struggle</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>3-5 Hours Daily</strong> - Creating and posting content</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>Writer's Block</strong> - Struggling to create engaging posts</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>Platform Juggling</strong> - Switching between 8+ apps</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>Forgotten Posts</strong> - Missing optimal posting times</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>Inconsistent Schedule</strong> - Posts scattered randomly</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>Rushed Content</strong> - Quality suffers under pressure</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>Burnout Risk</strong> - Exhausted from constant posting</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-3 text-xl">❌</span>
                        <span className="text-gray-600"><strong>No Analytics</strong> - Guessing what works</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Time Savings Highlight */}
              <div className="text-center mt-8 p-8 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg text-white shadow-2xl">
                <h4 className="text-4xl font-extrabold mb-4 text-white drop-shadow-lg">Save 20+ Hours Per Week! ⏱️</h4>
                <p className="text-xl font-semibold mb-6 text-white drop-shadow-md">FrenzyPost users save an average of 20 hours weekly on social media management</p>
                <p className="text-lg font-medium text-white drop-shadow-sm">👆 Choose your plan above and start your 3-day FREE trial now!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionOnboarding; 